import { CheckOutIdentity } from "../types";
import {
  CHECK_OUT_STAMP_ROOT,
  buildCheckOutStamp,
  parseCheckOutStamp,
} from "../shared/checkOutStamp";

// Word's route to the <jyloCheckOut> stamp: Word.customXmlParts, read with the same
// load/sync pattern as the assembly metadata in ./assembly. What the stamp means and
// how it is parsed lives in ../shared/checkOutStamp -- only the plumbing is here.
//
// Word keeps its own reader rather than going through ../shared/customXmlIdentity,
// which Excel and PowerPoint share: those two expose getXml() and this exposes an `xml`
// property after a load, which is a different enough shape that sharing it would mean
// a flag.

export async function readCheckOutIdentity(): Promise<CheckOutIdentity | null> {
  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts;
      context.load(parts, "items");
      await context.sync();

      // Load every part's xml in one batch. Syncing per part is a round trip to Word
      // each time, and this runs on every document open.
      parts.items.forEach((part) => context.load(part, "xml"));
      await context.sync();

      for (const part of parts.items) {
        const identity = parseCheckOutStamp(part.xml);
        if (identity) {
          return identity;
        }
      }

      return null;
    });
  } catch (error) {
    // Rethrown rather than flattened to null: see ../shared/customXmlIdentity. An
    // absent stamp and a failed read must not read as the same thing to the user.
    console.log("Error reading check out identity: " + error);
    throw error;
  }
}

/**
 * Re-writes the identity part into the live document -- the recovery path when a
 * check in fails AFTER the part was removed: the draft is still open, so the
 * document must keep saying which draft it is.
 */
export async function writeCheckOutIdentity(identity: CheckOutIdentity): Promise<void> {
  await Word.run(async (context) => {
    context.document.customXmlParts.add(buildCheckOutStamp(identity));
    await context.sync();
  });
}

export { CHECK_OUT_STAMP_ROOT };
